import { SchedulerItem, SchedulerItemContent, SchedulerItemMouseEvent, SchedulerItemProps, SchedulerViewItem, SchedulerViewItemProps } from "@progress/kendo-react-scheduler";
import { useRef } from "react";
import { AttendanceStatus } from "../../../data/entities";
import { to12HourTime } from "../../../utils";

// Open the editor on single click (Kendo's default is double click).
// A click also fires on mouse-release after a drag or resize, so only clicks
// with no meaningful mouse travel open the editor; the delete X and resize
// handles keep their own behavior.
const useOpenEditorOnClick = (props: Pick<SchedulerItemProps, "onClick" | "onDoubleClick" | "onMouseDown">) => {
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  const onMouseDown = (e: SchedulerItemMouseEvent) => {
    props.onMouseDown?.(e);
    mouseDownPos.current = { x: e.syntheticEvent.clientX, y: e.syntheticEvent.clientY };
  };

  const onClick = (e: SchedulerItemMouseEvent) => {
    props.onClick?.(e);

    const target = e.syntheticEvent.target as HTMLElement;
    if (target.closest?.(".k-event-delete, .k-resize-handle")) return;

    const down = mouseDownPos.current;
    const dragged = down &&
      (Math.abs(e.syntheticEvent.clientX - down.x) > 5 ||
       Math.abs(e.syntheticEvent.clientY - down.y) > 5);
    if (dragged) return;

    props.onDoubleClick?.(e);
  };

  return { onMouseDown, onClick };
};

interface CalendarViewItemProps extends SchedulerViewItemProps {
  customStyling?: React.CSSProperties;
}

export const CalendarViewItem = (props: CalendarViewItemProps) => {
  const mergeStyle = {
    ...props.style,
    ...props.customStyling
  }
  return (
    <SchedulerViewItem {...props} style={mergeStyle} />
  );
};

export const TitleStartItem = (props: SchedulerItemProps) => {
  const { dataItem } = props;
  const { onMouseDown, onClick } = useOpenEditorOnClick(props);
  return (
    <SchedulerItem
      {...props}
      onMouseDown={onMouseDown}
      onClick={onClick}
      style={{
        ...props.style,
        display: "flex",
        justifyContent: "space-between",
        padding: 0,
        paddingRight: "1.5rem",
        backgroundColor:dataItem.serviceTypeColor
      }}
    >
      <SchedulerItemContent>{props.title}</SchedulerItemContent>
      <SchedulerItemContent>
        {to12HourTime(props.dataItem.startTime)}
      </SchedulerItemContent>
    </SchedulerItem>
  );
};

interface NameTitleTimeItemProps extends SchedulerItemProps {
  onItemContextMenu?: (e: React.MouseEvent, dataItem: any) => void;
}

export const NameTitleTimeItem = (props: NameTitleTimeItemProps) => {
  const { dataItem, onItemContextMenu, ...itemProps } = props;
  const { onMouseDown, onClick } = useOpenEditorOnClick(itemProps);

  const isCheckedIn = dataItem.attendanceStatus === AttendanceStatus.CheckedIn;
  const isNoShow = dataItem.attendanceStatus === AttendanceStatus.NoShow;

  return (
    <SchedulerItem
      {...itemProps}
      dataItem={dataItem}
      onMouseDown={onMouseDown}
      onClick={onClick}
      className={[itemProps.className, (isCheckedIn || isNoShow) ? "appt-status-banner" : undefined]
        .filter(Boolean)
        .join(" ") || undefined}
      style={{ padding: 0, backgroundColor: dataItem.serviceTypeColor }}
    >
      <div
        style={{ height: "100%", lineHeight: 1.2 }}
        onContextMenu={onItemContextMenu ? (e) => onItemContextMenu(e, dataItem) : undefined}
      >
        {(isCheckedIn || isNoShow) && (
          <div
            className={`${isCheckedIn ? "bg-success" : "bg-danger"} text-white fw-bold text-uppercase d-flex align-items-center gap-1`}
            style={{ fontSize: 10, letterSpacing: "0.5px", padding: "0 6px", height: "var(--appt-banner-height, 16px)" }}
          >
            <i className={`fa ${isCheckedIn ? "fa-check" : "fa-ban"}`} />
            {isCheckedIn ? "Checked in" : "No show"}
          </div>
        )}
        <div
          className="d-flex justify-content-between flex-wrap align-items-center"
          style={{ backgroundColor: dataItem.serviceTypeColor, paddingRight: "1.5rem", paddingTop: "var(--appt-content-pad-top, 3px)" }}
        >
          <SchedulerItemContent className="flex-shrink-1 fw-bold py-0 d-flex align-items-center gap-1">
            {dataItem.notes?.trim() && (
              <i className="fa fa-comment flex-shrink-0" style={{ fontSize: 10, opacity: 0.85 }} title="Has notes" />
            )}
            {props.dataItem.customerName}
          </SchedulerItemContent>
          <SchedulerItemContent className="flex-shrink-0 py-0">
            {to12HourTime(props.dataItem.startTime)}
          </SchedulerItemContent>
        </div>
        <SchedulerItemContent className="py-0" style={{ fontSize: 12 }}>
          {props.title}
        </SchedulerItemContent>
      </div>
    </SchedulerItem>
  );
};


